import { BaseNodeModel } from "@/models/nodes/BaseNodeModel";
import { WrapperNodeModel } from "@/models/nodes/WrapperNodeModel";

import type { DocumentModel } from "@/models/DocumentModel";
import type { NodeId, NodeModel } from "@/models/nodes/types";
import type { VisitableNode, NodeVisitor } from "@/models/nodes/visitors/NodeVisitor";
import type { TextNodeProps, TextNodeConstructorProps } from "@corca/data";

export class TextNodeModel extends BaseNodeModel<TextNodeProps> implements VisitableNode {
  static type = "text" as const;

  type = TextNodeModel.type;

  protected fillRequiredProps(props: TextNodeConstructorProps): TextNodeProps {
    this.reportMissingProps(props, ["value"]);
    return { ...props, value: props.value ?? "" };
  }

  static create(
    doc: DocumentModel,
    value: string = "",
    props: Omit<TextNodeConstructorProps, "value"> = {},
    nodes: NodeModel[] = [],
    id?: NodeId,
  ) {
    return new TextNodeModel(doc, id, { value, ...props }, nodes);
  }

  get wrapperNodes() {
    return this.children.filter((n) => n.is(WrapperNodeModel));
  }

  /**
   * Value is used only to populate text editor with initial content if needed.
   * After text editor initialization you should not use it to render text.
   */
  get value(): string {
    return this.props.value;
  }

  set value(value: string) {
    this.updateProps({ value });
  }

  accept<R>(visitor: NodeVisitor<R>): R {
    return visitor.visitTextNode(this);
  }
}
